/**
 * Invitee validation modal — same shell as shared ValidationMessageModal,
 * styled via invitee feature CSS (no dependency on application feature styles).
 */

import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { X, CircleAlert } from "lucide-react";
import modalStyles from "../styles/modals.module.css";

function messageLooksLikeHtml(message: string): boolean {
  return /<[a-z][\s\S]*>/i.test(message.trim());
}

interface InviteeValidationMessageModalProps {
  isOpen: boolean;
  title?: string;
  message: string;
  onClose: () => void;
  customButtons?: ReactNode;
  showValidationIcon?: boolean;
  overlayDataTestId?: string;
  closeButtonDataTestId?: string;
  okButtonDataTestId?: string;
}

export function InviteeValidationMessageModal({
  isOpen,
  title = "Validation Message",
  message,
  onClose,
  customButtons,
  showValidationIcon: showValidationIconProp,
  overlayDataTestId,
  closeButtonDataTestId,
  okButtonDataTestId,
}: InviteeValidationMessageModalProps) {
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const showValidationIcon =
    showValidationIconProp ??
    (title === "Validation Message" && messageLooksLikeHtml(message));

  const plainBody = messageLooksLikeHtml(message) ? (
    <div
      className={`${modalStyles.rentConfirmMessage} validation-message-html`}
      dangerouslySetInnerHTML={{ __html: message }}
    />
  ) : (
    <p className={`${modalStyles.rentConfirmMessage} whitespace-pre-line`}>{message}</p>
  );

  const content = (
    <div
      className={`${modalStyles.modalOverlay} !z-[10001]`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="invitee-validation-message-title"
      data-testid={overlayDataTestId}
    >
      <div
        className={`${modalStyles.modalContent} ${modalStyles.modalContentCompact}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={modalStyles.validationCompactHeader}>
          <h3 id="invitee-validation-message-title">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            data-testid={closeButtonDataTestId}
            className={`${modalStyles.closeButton} ${modalStyles.validationCompactClose}`}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className={modalStyles.validationCompactBody}>
          {showValidationIcon ? (
            <div className={modalStyles.validationMessageIconRow}>
              <div className={modalStyles.validationMessageIcon} aria-hidden>
                <CircleAlert className="h-4 w-4 text-red-600" strokeWidth={2} />
              </div>
              <div
                className={`${modalStyles.validationMessageHtmlWrap} validation-message-html`}
                dangerouslySetInnerHTML={{ __html: message }}
              />
            </div>
          ) : (
            plainBody
          )}
        </div>
        <div className={modalStyles.modalFooterConfirmTight}>
          {customButtons ?? (
            <button
              type="button"
              onClick={onClose}
              data-testid={okButtonDataTestId}
              className={modalStyles.btnPrimary}
            >
              Ok
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
